import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';
import { LeadDto } from './leads.service';

export type FranchiseListItem = {
  id: string;
  franchiseId?: string;
  name: string;
  code: string;
  slug: string;
  isActive: boolean;
  adminEmail: string;
  usersCount: number;
  leadsCount: number;
  createdAt?: string;
};

export type FranchiseDetail = {
  franchise: {
    id: string;
    franchiseId?: string;
    name: string;
    code: string;
    slug: string;
    isActive: boolean;
    adminEmail: string;
    contactPhone?: string;
    address?: {
      line1?: string;
      city?: string;
      state?: string;
      country?: string;
      postalCode?: string;
    };
    createdAt?: string;
    updatedAt?: string;
  };
  stats: {
    usersCount: number;
    leadsCount: number;
    activeCounselors: number;
  };
  staff: Array<{
    id: string;
    userId: string;
    email: string;
    role: string;
    isActive: boolean;
    firstName?: string;
    lastName?: string;
    lastLoginAt?: string;
  }>;
  recentLeads: LeadDto[];
};

export type CreateFranchisePayload = {
  name: string;
  code: string;
  adminEmail: string;
  contactPhone?: string;
  address?: FranchiseDetail['franchise']['address'];
};

export type CreateFranchiseResult = {
  franchise: FranchiseDetail['franchise'];
  tenantAdmin: {
    id: string;
    userId: string;
    email: string;
    role: string;
  };
};

@Injectable({ providedIn: 'root' })
export class FranchisesService {
  constructor(private readonly api: ApiService) {}

  list() {
    return this.api.get<ApiEnvelope<FranchiseListItem[]>>('platform/franchises').pipe(map((r) => r.data));
  }

  /** @param franchiseId Business tenant identifier, e.g. blr-b7c3e1 */
  getByFranchiseId(franchiseId: string) {
    return this.api
      .get<ApiEnvelope<FranchiseDetail>>(`platform/franchises/${franchiseId}`)
      .pipe(map((r) => r.data));
  }

  /** @deprecated Use getByFranchiseId */
  getById(franchiseId: string) {
    return this.getByFranchiseId(franchiseId);
  }

  create(payload: CreateFranchisePayload) {
    return this.api
      .post<ApiEnvelope<CreateFranchiseResult>>('platform/franchises', payload)
      .pipe(map((r) => r.data));
  }

  /** @param franchiseId Business tenant identifier */
  updateStatus(franchiseId: string, isActive: boolean) {
    return this.api
      .patch<ApiEnvelope<FranchiseDetail['franchise']>>(`platform/franchises/${franchiseId}/status`, { isActive })
      .pipe(map((r) => r.data));
  }
}
